import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Application bootstrap (loaded as a separate chunk from main.tsx)
 * ----------------------------------------------------------------
 * Everything heavy lives here so the entry chunk stays tiny and the
 * inline "Loading…" placeholder in template.html is visible to the
 * user before this chunk finishes downloading + parsing.
 *
 * Order of operations:
 *   1. Create the MSAL instance (synchronous, cheap).
 *   2. Render <App /> wrapped in <MsalProvider> immediately — the React
 *      tree mounts even though MSAL hasn't finished `initialize()` yet.
 *      MSAL hooks (`useMsal`) tolerate this and the redirect-handling
 *      Promise below kicks in as soon as it can.
 *   3. In an idle callback, run `initialize()` + `handleRedirectPromise`
 *      so they don't compete with the first paint of the React tree.
 */
import ReactDOM from 'react-dom/client';
import { EventType, PublicClientApplication, } from '@azure/msal-browser';
import App from './App';
import { BrowserRouter } from 'react-router-dom';
import { msalConfig } from './auth-config';
import { MsalProvider } from '@azure/msal-react';
export function bootstrap(rootEl) {
    const msalInstance = new PublicClientApplication(msalConfig);
    // Set the active account synchronously on every relevant MSAL event.
    msalInstance.addEventCallback((event) => {
        if ((event.eventType === EventType.LOGIN_SUCCESS ||
            event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
            event.eventType === EventType.SSO_SILENT_SUCCESS) &&
            event.payload.account) {
            msalInstance.setActiveAccount(event.payload.account);
        }
    });
    // Mount the React tree FIRST so the user sees app chrome (or the login
    // page) as soon as the bundle parses. MSAL initialization continues in
    // the background and is required only by hooks like `useMsal` /
    // protected routes — both of which already render their own loading
    // states until MSAL is ready.
    const root = ReactDOM.createRoot(rootEl);
    root.render(_jsx(MsalProvider, { instance: msalInstance, children: _jsx(BrowserRouter, { children: _jsx(App, {}) }) }));
    // Defer MSAL's initialize + handleRedirectPromise to an idle callback
    // so they don't compete with first paint. requestIdleCallback is not
    // available in Safari, so we fall back to a microtask.
    const runWhenIdle = (cb) => {
        if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(cb, { timeout: 1500 });
        }
        else {
            setTimeout(cb, 0);
        }
    };
    runWhenIdle(() => {
        msalInstance
            .initialize()
            .then(() => msalInstance.handleRedirectPromise())
            .then((response) => {
            var _a;
            if (response) {
                console.log('Login redirect successful', (_a = response.account) === null || _a === void 0 ? void 0 : _a.username);
                msalInstance.setActiveAccount(response.account);
            }
            else if (!msalInstance.getActiveAccount() &&
                msalInstance.getAllAccounts().length > 0) {
                msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
            }
        })
            .catch((error) => {
            console.error('Error handling redirect:', error);
        });
    });
}
